/**
 * Landing (spec 07): 7 pełnoekranowych sekcji ze scroll-snapem, scroll-spy,
 * cennik z przełącznikiem okresu i akordeon FAQ.
 *
 * [D19] Mock produktu w hero pokazuje układ interfejsu, ale nie zawiera
 * wymyślonych kwot ani liczników — decyzja właściciela produktu (2026-08-07).
 * Cennik korzysta z realnej oferty (§15: cennik to konfiguracja, nie demo).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Accordion,
  Badge,
  Bank,
  Button,
  Calendar,
  Check,
  Clock,
  Document,
  FeatureCard,
  GraduationCap,
  Info,
  Logo,
  PlanCard,
  Plug,
  SegmentedToggle,
  Shield,
  ShieldCheckIcon,
  Users,
  Wallet,
  Workflow,
  cx,
  type PlanCardFeature,
} from '@k1p4/ui'
import { formatPLN } from '@k1p4/i18n'
import { LanguageSwitcher } from '../../components/LanguageSwitcher'
import { localSignIn } from '../../api/client'
import { useAuthEntry } from '../../auth/useAuthEntry'
import styles from './Landing.module.css'
import { useDocumentTitle } from '../../components/useDocumentTitle'

/** Sekcje w kolejności ze spec 07 — indeks jest wspólny dla nav i kropek. */
const SECTION_KEYS = [
  'start',
  'features',
  'how',
  'security',
  'pricing',
  'faq',
  'contact',
] as const

/** Pozycje nawigacji głównej → indeks sekcji (spec 07 §0). */
const NAV_ITEMS = [
  { key: 'features', index: 1 },
  { key: 'how', index: 2 },
  { key: 'security', index: 3 },
  { key: 'pricing', index: 4 },
] as const

/*
 * `soon` — zapowiedź bez pokrycia w systemie dostaje plakietkę „WKRÓTCE”
 * (decyzja właściciela 2026-09-03, potwierdzona 2026-10-01; D107). Tam, gdzie
 * zapowiedź jest tylko częścią karty, mówi o niej sam opis.
 */
const MODULES = [
  { key: 'employees', Icon: Users, soon: false },
  { key: 'documents', Icon: Document, soon: false },
  { key: 'time', Icon: Clock, soon: false },
  { key: 'leaves', Icon: Calendar, soon: false },
  { key: 'payroll', Icon: Wallet, soon: false },
  { key: 'declarations', Icon: Bank, soon: false },
  { key: 'audit', Icon: Shield, soon: false },
  { key: 'integrations', Icon: Plug, soon: true },
  { key: 'growth', Icon: GraduationCap, soon: false },
] as const

const SECURITY_CARDS = [
  { key: 'sso', Icon: ShieldCheckIcon, soon: false },
  { key: 'roles', Icon: Users, soon: false },
  { key: 'audit', Icon: Shield, soon: false },
  { key: 'events', Icon: Workflow, soon: true },
] as const

/**
 * Realna oferta (§15), w groszach — liczymy na liczbach całkowitych, a pokazujemy
 * przez formatPLN (fala 9, grupa 154). Cena roczna to 80 % miesięcznej zaokrąglone
 * do pełnego złotego, jak w makiecie (spec 07 §5: 12 → 10 zł, 19 → 15 zł).
 */
const PLANS = [
  { key: 'start', name: 'START', monthly: 1200, featured: false },
  { key: 'pro', name: 'PRO', monthly: 1900, featured: true },
  { key: 'ent', name: 'ENTERPRISE', monthly: null, featured: false },
] as const

/** Cechy planów (spec 07 §5); teksty w i18n, `soon` jak w kartach wyżej (D107). */
const PLAN_FEATURES: Record<(typeof PLANS)[number]['key'], ReadonlyArray<{ key: string; soon?: true }>> = {
  start: [
    { key: 'upTo25' },
    { key: 'hrTimeLeave' },
    { key: 'payrollFiles' },
    { key: 'signature', soon: true },
    { key: 'emailSupport' },
  ],
  pro: [
    { key: 'unlimited' },
    { key: 'allStart' },
    { key: 'integrations', soon: true },
    { key: 'workflowReports' },
    { key: 'auditProof' },
    { key: 'prioritySupport' },
  ],
  ent: [
    { key: 'multiTenant' },
    { key: 'sso', soon: true },
    { key: 'hyperledger', soon: true },
    { key: 'api', soon: true },
    { key: 'sla' },
    { key: 'rollout' },
  ],
}

/*
 * Kolumna „Produkt” prowadzi do sekcji strony, jak w makiecie (spec 07 §8;
 * fala 9b, grupa 154). „Podręcznik użytkownika” prowadzi do /docs (ADR-0062).
 * Pozostałe pozycje nie mają jeszcze stron, do których mogłyby prowadzić,
 * więc zostają tekstem — link donikąd byłby gorszy.
 */
const FOOTER_SECTION: Partial<Record<string, number>> = {
  features: 1,
  security: 3,
  pricing: 4,
  integrations: 1,
}

const FOOTER_COLUMNS = [
  { key: 'product', items: ['features', 'pricing', 'security', 'integrations'] },
  { key: 'audience', items: ['smallBusiness', 'accountingOffices', 'groups', 'hrTeams'] },
  { key: 'company', items: ['about', 'contact', 'status', 'apiDocs', 'help'] },
] as const

/**
 * Dokąd prowadzi „Zobacz demo": na produkcji pod `demo.<domena>`, gdzie stoi
 * obraz `web-demo` na fiksturach (ADR-0059) — adres z domeny obszarów, którą
 * serwer i tak podaje w `/auth/entry`, a nie z literału, żeby instancja testowa
 * nie odsyłała na demo produkcji. W samym obrazie demo, i dopóki domeny nie
 * znamy, przycisk prowadzi na ekran wejścia, jak przed demem.
 */
function useDemoHref(): string | null {
  const entry = useAuthEntry()
  if (localSignIn() !== null) return null
  if (entry.status !== 'ready' || !entry.entry.tenantHostSuffix) return null
  return `${globalThis.location.protocol}//demo.${entry.entry.tenantHostSuffix}/`
}

export function Landing() {
  useDocumentTitle()
  const { t } = useTranslation()
  const demoHref = useDemoHref()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [yearly, setYearly] = useState(false)

  /* Scroll-spy: sonda na 40% wysokości viewportu, throttling przez rAF. */
  useEffect(() => {
    const element = scrollRef.current
    if (!element) return
    let frame = 0
    function handleScroll(): void {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const node = scrollRef.current
        if (!node) return
        const probe = node.scrollTop + node.clientHeight * 0.4
        let index = 0
        for (const [i, child] of [...node.children].entries()) {
          if ((child as HTMLElement).offsetTop <= probe) index = i
        }
        setActiveIndex((current) => (current === index ? current : index))
      })
    }
    element.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      element.removeEventListener('scroll', handleScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  const goTo = useCallback((index: number) => {
    const node = scrollRef.current
    const child = node?.children[index] as HTMLElement | undefined
    if (!node || !child) return
    node.scrollTo({ top: child.offsetTop, behavior: 'smooth' })
  }, [])

  const price = (monthly: number | null): string => {
    if (monthly === null) return t('landing.pricing.customPrice')
    return formatPLN(yearly ? Math.round((monthly * 4) / 5) : monthly, { grosze: 'never' })
  }

  const soonBadge = (tone: 'warning' | 'warningOnDark') => <Badge tone={tone}>{t('soon.title')}</Badge>

  const planFeatures = (plan: (typeof PLANS)[number]['key']): PlanCardFeature[] =>
    PLAN_FEATURES[plan].map((feature) => ({
      label: t(`landing.pricing.features.${plan}.${feature.key}`),
      ...(feature.soon ? { badge: soonBadge('warning') } : {}),
    }))

  const trust = t('landing.hero.trust', { returnObjects: true }) as string[]

  return (
    <div className={styles.app}>
      <header className={styles.topbar}>
        <div className={styles.topbarInner}>
          <button type="button" className={styles.brandButton} onClick={() => goTo(0)}>
            <Logo size={38} withWordmark withTagline />
          </button>
          <nav className={styles.mainNav} aria-label={t('shell.mainNav')}>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={cx(styles.navLink, activeIndex === item.index && styles.navLinkActive)}
                onClick={() => goTo(item.index)}
              >
                {t(`landing.nav.${item.key}`)}
              </button>
            ))}
          </nav>
          <div className={styles.topbarRight}>
            <LanguageSwitcher />
            <Link to="/login" className={styles.loginLink}>
              {t('landing.nav.login')}
            </Link>
            {/*
              „Wypróbuj za darmo" w menu prowadzi do demo, jak „Zobacz demo"
              (decyzja właściciela 2026-10-01) — człowiek, który chce
              spróbować, ogląda działający system bez zakładania konta.
              Rejestracja zostaje przy pozostałych przyciskach i w cenniku.
              Bez znanej domeny demo przycisk wraca do rejestracji.
            */}
            {demoHref ? (
              <a href={demoHref}>
                <Button size="sm">{t('landing.nav.cta')}</Button>
              </a>
            ) : (
              <Link to="/register">
                <Button size="sm">{t('landing.nav.cta')}</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className={styles.scroll} ref={scrollRef}>
        {/* ── 1. Hero ──────────────────────────────────────────────── */}
        <section className={styles.section} aria-label={t('landing.sections.start')}>
          <div className={styles.sectionInner}>
            <div className={styles.heroGrid}>
              <div>
                <span className={styles.badge}>
                  <span className={styles.badgeDot} aria-hidden="true" />
                  {t('landing.hero.badge')}
                </span>
                <h1 className={styles.heroTitle}>{t('landing.hero.title')}</h1>
                <p className={styles.heroLead}>{t('landing.hero.lead')}</p>
                <div className={styles.heroActions}>
                  <Link to="/register">
                    <Button size="lg">{t('landing.hero.ctaPrimary')}</Button>
                  </Link>
                  {demoHref ? (
                    /* Zwykły `<a>`: demo stoi pod innym hostem, router SPA nie ma tu nic do roboty. */
                    <a href={demoHref}>
                      <Button size="lg" variant="secondary">
                        {t('landing.hero.ctaSecondary')}
                      </Button>
                    </a>
                  ) : (
                    <Link to="/login">
                      <Button size="lg" variant="secondary">
                        {t('landing.hero.ctaSecondary')}
                      </Button>
                    </Link>
                  )}
                </div>
                <ul className={styles.ticks}>
                  {(['tick1', 'tick2', 'tick3'] as const).map((tick) => (
                    <li key={tick} className={styles.tick}>
                      <Check size={15} className={styles.tickIcon} />
                      {t(`landing.hero.${tick}`)}
                    </li>
                  ))}
                </ul>
              </div>

              {/*
                Mock produktu: układ i akcje 1:1 z makietą, wartości puste [D19].
                Zamiast wymyślonych kwot — pauza i nota, że instancja startuje pusta.
              */}
              <div>
                <div className={styles.mock}>
                  <div className={styles.mockChrome}>
                    <span className={styles.mockDot} aria-hidden="true" />
                    <span className={styles.mockDot} aria-hidden="true" />
                    <span className={styles.mockDot} aria-hidden="true" />
                    <span className={styles.mockAddress}>{t('landing.hero.mock.address')}</span>
                  </div>
                  <div className={styles.mockBody}>
                    <p className={styles.mockKicker}>{t('landing.hero.mock.kicker')}</p>
                    <p className={styles.mockTitle}>{t('landing.hero.mock.title')}</p>
                    <div className={styles.mockTiles}>
                      {(['costLabel', 'employeesLabel'] as const).map((label) => (
                        <div key={label} className={styles.mockTile}>
                          <p className={styles.mockTileLabel}>{t(`landing.hero.mock.${label}`)}</p>
                          <p className={styles.mockTileValue}>{t('landing.hero.mock.emptyValue')}</p>
                        </div>
                      ))}
                    </div>
                    <p className={styles.mockNote}>
                      <Info size={15} />
                      {t('landing.hero.mock.emptyNote')}
                    </p>
                    <div className={styles.mockActions}>
                      <Button size="sm" disabled>
                        {t('landing.hero.mock.primaryAction')}
                      </Button>
                      <Button size="sm" variant="secondary" disabled>
                        {t('landing.hero.mock.secondaryAction')}
                      </Button>
                    </div>
                  </div>
                </div>
                <p className={styles.mockCaption}>{t('landing.hero.mock.caption')}</p>
              </div>
            </div>

            <div className={styles.trust}>
              <span className={styles.trustLabel}>{t('landing.hero.trustLabel')}</span>
              <ul className={styles.trustList}>
                {trust.map((item) => (
                  <li key={item} className={styles.trustItem}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <button type="button" className={styles.hint} onClick={() => goTo(1)}>
              <span className={styles.hintMouse} aria-hidden="true">
                <span className={styles.hintWheel} />
              </span>
              <span className={styles.hintLabel}>{t('landing.hero.scroll')}</span>
            </button>
          </div>
        </section>

        {/* ── 2. Funkcje ───────────────────────────────────────────── */}
        <section
          className={cx(styles.section, styles.onSurface)}
          aria-label={t('landing.sections.features')}
        >
          <div className={cx(styles.sectionInner, styles.centered)}>
            <p className={styles.eyebrow}>{t('landing.features.eyebrow')}</p>
            <h2 className={styles.sectionTitle}>{t('landing.features.title')}</h2>
            <p className={styles.sectionSubtitle}>{t('landing.features.subtitle')}</p>
            <div className={styles.grid3}>
              {MODULES.map(({ key, Icon, soon }) => (
                <FeatureCard
                  key={key}
                  icon={<Icon size={22} />}
                  {...(soon ? { badge: soonBadge('warning') } : {})}
                  title={t(`landing.features.${key}`)}
                  description={t(`landing.features.${key}Desc`)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ── 3. Jak to działa ─────────────────────────────────────── */}
        <section className={styles.section} aria-label={t('landing.sections.how')}>
          <div className={styles.sectionInner}>
            <div className={styles.howCard}>
              <div>
                <p className={styles.eyebrow}>{t('landing.how.eyebrow')}</p>
                <h2 className={styles.sectionTitle}>{t('landing.how.title')}</h2>
                <p className={styles.sectionSubtitle}>{t('landing.how.lead')}</p>
                <Link to="/register">
                  <Button size="lg">{t('landing.how.cta')}</Button>
                </Link>
              </div>
              <div className={styles.steps}>
                {([1, 2, 3, 4] as const).map((step) => (
                  <div key={step} className={styles.stepCard}>
                    <span className={styles.stepNumber} aria-hidden="true">
                      {step}
                    </span>
                    <div>
                      <p className={styles.stepTitle}>{t(`landing.how.step${step}`)}</p>
                      <p className={styles.stepDesc}>{t(`landing.how.step${step}Desc`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── 4. Bezpieczeństwo ────────────────────────────────────── */}
        <section
          className={cx(styles.section, styles.onDark)}
          aria-label={t('landing.sections.security')}
        >
          <div className={cx(styles.sectionInner, styles.centered)}>
            <p className={styles.eyebrow}>{t('landing.security.eyebrow')}</p>
            <h2 className={styles.sectionTitle}>{t('landing.security.title')}</h2>
            <p className={styles.sectionSubtitle}>{t('landing.security.subtitle')}</p>
            <div className={styles.grid4}>
              {SECURITY_CARDS.map(({ key, Icon, soon }) => (
                <FeatureCard
                  key={key}
                  tone="dark"
                  icon={<Icon size={21} />}
                  {...(soon ? { badge: soonBadge('warningOnDark') } : {})}
                  title={t(`landing.security.${key}`)}
                  description={t(`landing.security.${key}Desc`)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ── 5. Cennik ────────────────────────────────────────────── */}
        <section
          className={cx(styles.section, styles.onSurface)}
          aria-label={t('landing.sections.pricing')}
        >
          <div className={cx(styles.sectionInner, styles.centered)}>
            <p className={styles.eyebrow}>{t('landing.pricing.eyebrow')}</p>
            <h2 className={styles.sectionTitle}>{t('landing.pricing.title')}</h2>
            <div className={styles.pricingToggle}>
              <SegmentedToggle
                label={t('landing.pricing.title')}
                value={yearly ? 'yearly' : 'monthly'}
                onChange={(value) => setYearly(value === 'yearly')}
                options={[
                  { value: 'monthly', label: t('landing.pricing.monthly') },
                  {
                    value: 'yearly',
                    label: t('landing.pricing.yearly'),
                    suffix: t('landing.pricing.yearlyDiscount'),
                  },
                ]}
              />
            </div>
            <div className={styles.grid3}>
              {PLANS.map((plan) => (
                <PlanCard
                  key={plan.key}
                  name={plan.name}
                  tagline={t(`landing.pricing.${plan.key}Tagline`)}
                  price={price(plan.monthly)}
                  unit={
                    plan.monthly === null
                      ? t('landing.pricing.customUnit')
                      : t('landing.pricing.unit')
                  }
                  billNote={
                    plan.monthly === null
                      ? t('landing.pricing.billCustom')
                      : yearly
                        ? t('landing.pricing.billYearly')
                        : t('landing.pricing.billMonthly')
                  }
                  featured={plan.featured}
                  featuredLabel={t('landing.pricing.featured')}
                  features={planFeatures(plan.key)}
                  cta={
                    <Link to="/register">
                      <Button
                        variant={plan.featured ? 'primary' : 'secondary'}
                        className={styles.sectionInner}
                      >
                        {t(`landing.pricing.${plan.key}Cta`)}
                      </Button>
                    </Link>
                  }
                />
              ))}
            </div>
          </div>
        </section>

        {/* ── 6. FAQ ───────────────────────────────────────────────── */}
        <section className={styles.section} aria-label={t('landing.sections.faq')}>
          <div className={cx(styles.sectionInner, styles.faqWrap)}>
            <h2 className={styles.faqTitle}>{t('landing.faq.title')}</h2>
            <Accordion
              items={([1, 2, 3, 4] as const).map((index) => ({
                key: `q${index}`,
                question: t(`landing.faq.q${index}`),
                answer: t(`landing.faq.a${index}`),
              }))}
            />
          </div>
        </section>

        {/* ── 7. CTA końcowe + stopka ──────────────────────────────── */}
        <section
          className={cx(styles.section, styles.onSurface)}
          aria-label={t('landing.sections.contact')}
        >
          <div className={styles.sectionInner}>
            <div className={styles.closing}>
              <h2 className={styles.closingTitle}>{t('landing.closing.title')}</h2>
              <p className={styles.closingBody}>{t('landing.closing.body')}</p>
              <Link to="/register">
                <Button size="lg" variant="secondary">
                  {t('landing.closing.cta')}
                </Button>
              </Link>
            </div>

            <footer className={styles.footer}>
              <div className={styles.footerGrid}>
                <div>
                  <Logo size={34} withWordmark />
                  <p className={styles.footerDescription}>{t('landing.footer.description')}</p>
                </div>
                {FOOTER_COLUMNS.map((column) => (
                  <div key={column.key}>
                    <p className={styles.footerHeading}>{t(`landing.footer.${column.key}`)}</p>
                    <ul className={styles.footerList}>
                      {column.items.map((item) => {
                        const label = t(`landing.footer.items.${column.key}.${item}`)
                        const section = column.key === 'product' ? FOOTER_SECTION[item] : undefined
                        return (
                          <li key={item}>
                            {item === 'help' ? (
                              <Link to="/docs" className={styles.footerLink}>
                                {label}
                              </Link>
                            ) : section === undefined ? (
                              label
                            ) : (
                              <button type="button" className={styles.footerLink} onClick={() => goTo(section)}>
                                {label}
                              </button>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ))}
              </div>
              <div className={styles.footerBottom}>
                <span>{t('landing.footer.copyright')}</span>
                <span>{t('landing.footer.compliance')}</span>
              </div>
            </footer>
          </div>
        </section>
      </div>

      <nav className={styles.dots} aria-label={t('shell.mainNav')}>
        {SECTION_KEYS.map((key, index) => (
          <button
            key={key}
            type="button"
            className={cx(styles.dot, activeIndex === index && styles.dotActive)}
            aria-label={t(`landing.sections.${key}`)}
            aria-current={activeIndex === index ? 'true' : undefined}
            title={t(`landing.sections.${key}`)}
            onClick={() => goTo(index)}
          />
        ))}
      </nav>

    </div>
  )
}
